import { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import PartialDateInput from '../components/shared/PartialDateInput.jsx'
import PlaceInput from '../components/shared/PlaceInput.jsx'
import UpgradeInline from '../components/shared/UpgradeInline.jsx'
import { GENDER_OPTIONS } from '../utils/constants.js'

const TEXT_GROUPS = [
  {
    label: 'Identity',
    fields: [
      { key: 'first_name',     label: 'All given names *',       type: 'text',   placeholder: 'e.g. Johannes Wilhelmus Petrus', required: true },
      { key: 'preferred_name', label: 'Preferred name',          type: 'text',   placeholder: 'The name they actually used, e.g. Jan' },
      { key: 'last_name',      label: 'Last name *',             type: 'text',   placeholder: 'e.g. de Vries', required: true },
      { key: 'birth_name',     label: 'Birth name (if different)', type: 'text', placeholder: 'Maiden name or birth surname' },
      { key: 'gender',         label: 'Gender',                  type: 'select', options: GENDER_OPTIONS },
    ]
  },
  {
    label: 'Life',
    fields: [
      { key: 'occupation', label: 'Occupation', type: 'text',     placeholder: 'e.g. Greengrocer, farmer, teacher' },
      { key: 'notes',      label: 'Notes',      type: 'textarea', placeholder: 'Any details you know: neighbourhood, religion, migration, stories passed down...' },
    ]
  },
]

export default function AddPersonPage() {
  usePageTitle('Add Person')
  const { treeId } = useParams()
  const navigate = useNavigate()
  // Pre-link mode: /add-person?relate_to=<id>&relation=<parent|partner|child>
  // After creating the new person, auto-create the relationship so users
  // can click the '+' ghost on a tree node and land here ready to fill in
  // just the identity fields — no second step needed.
  const [searchParams] = useSearchParams()
  const relateTo  = searchParams.get('relate_to')
  const relation  = searchParams.get('relation') // parent | partner | child
  const [form, setForm] = useState({
    first_name: '', preferred_name: '', last_name: '', birth_name: '', gender: '',
    birth_date: '', birth_place: '', death_date: '', death_place: '',
    cause_of_death: '', occupation: '', notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // Tier-limit errors render as an inline upgrade CTA rather than a
  // plain error string, so the fix is one click away. Everything else
  // stays in the existing `error` state.
  const [tierError, setTierError] = useState(null)

  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required.')
      return
    }
    setSaving(true)
    setError(null)
    setTierError(null)
    try {
      const person = await api.post('/persons', { ...form, tree_id: treeId })

      // If launched from a '+' affordance, auto-create the relationship.
      // Errors here are non-fatal — the person still exists; we just route
      // to them and the user can wire the connection manually.
      if (relateTo && relation) {
        const relBody = relation === 'parent'
          ? { tree_id: treeId, person_a_id: person.id,  person_b_id: relateTo, type: 'parent_child' }
          : relation === 'child'
            ? { tree_id: treeId, person_a_id: relateTo, person_b_id: person.id, type: 'parent_child' }
            : relation === 'partner'
              ? { tree_id: treeId, person_a_id: relateTo, person_b_id: person.id, type: 'partner' }
              : null
        if (relBody) {
          try { await api.post('/relationships', relBody) } catch { /* non-fatal */ }
        }
      }

      navigate(`/tree/${treeId}/person/${person.id}`)
    } catch (err) {
      if (err.code === 'TIER_LIMIT') setTierError(err)
      else setError(err.message || 'Something went wrong. Please try again.')
      setSaving(false)
    }
  }

  const fullName = [form.first_name, form.last_name].filter(Boolean).join(' ')

  return (
    <div>
      <div className="page-header">
        <div className="page-header__top">
          <div>
            <div className="page-header__label">
              <span style={{ cursor: 'pointer' }} onClick={() => navigate(`/tree/${treeId}`)}>
                ← Back to tree
              </span>
            </div>
            <h1 className="page-header__title">
              {relation === 'parent'  ? 'Add a parent' :
               relation === 'partner' ? 'Add a partner' :
               relation === 'child'   ? 'Add a child' :
               'Add a person'}
            </h1>
            <div className="page-header__meta">
              The more you provide, the richer the AI-generated context will be
            </div>
          </div>
        </div>
      </div>

      <div className="add-person-page">
        <form onSubmit={handleSubmit} className="add-person-form">

          {TEXT_GROUPS.map(group => (
            <div key={group.label} className="form-group">
              <div className="form-group__label">{group.label}</div>
              <div className="form-group__fields">
                {group.fields.map(field => (
                  <div key={field.key} className="form-field">
                    <label className="form-field__label">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        className="form-input form-input--textarea"
                        value={form[field.key]}
                        placeholder={field.placeholder}
                        onChange={e => set(field.key, e.target.value)}
                        rows={4}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        className="form-input form-input--select"
                        value={form[field.key]}
                        onChange={e => set(field.key, e.target.value)}
                      >
                        {field.options.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        className="form-input"
                        value={form[field.key]}
                        placeholder={field.placeholder}
                        onChange={e => set(field.key, e.target.value)}
                        required={field.required || false}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="form-group">
            <div className="form-group__label">Birth</div>
            <div className="form-group__fields">
              <div className="form-field">
                <label className="form-field__label">Birth date</label>
                <PartialDateInput value={form.birth_date} onChange={v => set('birth_date', v)} />
                <span className="form-field__hint">Year is enough. Add month and day if known.</span>
              </div>
              <div className="form-field">
                <label className="form-field__label">Birth place</label>
                <PlaceInput value={form.birth_place}
                  placeholder="e.g. Rotterdam, Netherlands"
                  onChange={v => set('birth_place', v)} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <div className="form-group__label">Death</div>
            <div className="form-group__fields">
              <div className="form-field">
                <label className="form-field__label">Death date</label>
                <PartialDateInput value={form.death_date} onChange={v => set('death_date', v)} />
                <span className="form-field__hint">Leave empty if unknown or still alive.</span>
              </div>
              <div className="form-field">
                <label className="form-field__label">Death place</label>
                <PlaceInput value={form.death_place}
                  placeholder="e.g. Amsterdam, Netherlands"
                  onChange={v => set('death_place', v)} />
              </div>
              <div className="form-field">
                <label className="form-field__label">Cause of death</label>
                <input className="form-input" value={form.cause_of_death}
                  placeholder="e.g. Pneumonia"
                  onChange={e => set('cause_of_death', e.target.value)} />
                <span className="form-field__hint">Useful for family medical history.</span>
              </div>
            </div>
          </div>

          {tierError && <UpgradeInline error={tierError} />}
          {error && (
            <div className="form-error">{error}</div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => navigate(`/tree/${treeId}`)}
              style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Adding…' : fullName ? `Add ${fullName}` : 'Add person'}
            </button>
          </div>

        </form>

        <div className="add-person-aside">
          <div className="aside-hint">
            <div className="aside-hint__icon">✦</div>
            <h3>What makes a rich story</h3>
            <p>Genealogic uses occupation, birth place, and dates to locate your ancestor in historical events: famines, wars, economic crises, migrations.</p>
            <p>A greengrocer in Rotterdam in 1940 is a very different person to a farmer in Zeeland in 1840. The more specific you are, the more specific the history.</p>
            <p style={{ color: 'var(--gold)', fontStyle: 'italic', marginTop: '1rem' }}>
              You can always add more information later.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
